import { memo, useRef, useLayoutEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { EASE_OUT, EASE_IN, SPRING } from '../../theme/motion'

const SWAP = { type: 'spring', stiffness: 500, damping: 30 }

/** Pulsing orb: rings ripple out while listening, the core swells while speech is heard. */
function VoiceOrb({ hearing, size }) {
  return (
    <div className="chat-orb" style={{ width: size, height: size }}>
      {[0, 1, 2].map(i => (
        <motion.span
          key={i}
          className="chat-orb-ring"
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: hearing ? 1.75 : 1.4, opacity: [0, hearing ? 0.55 : 0.3, 0] }}
          transition={{ duration: hearing ? 1.1 : 2, repeat: Infinity, delay: i * (hearing ? 0.36 : 0.66), ease: 'easeOut' }}
        />
      ))}
      <motion.span
        className="chat-orb-core"
        animate={hearing ? { scale: [1, 1.14, 0.96, 1.1, 1] } : { scale: [1, 1.04, 1] }}
        transition={{ duration: hearing ? 0.9 : 2.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: size * 0.42 }}>mic</span>
      </motion.span>
    </div>
  )
}

/** Listening view: orb, live transcript, cancel / done. Replaces the input row. */
function Listening({ phone, hearing, interim, onDone, onCancel }) {
  return (
    <motion.div
      key="listening"
      className={`chat-listening ${phone ? 'chat-listening-lg' : ''}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8, transition: { duration: 0.14, ease: EASE_IN } }}
      transition={{ duration: 0.28, ease: EASE_OUT }}
    >
      <button type="button" className="chat-orb-btn" onClick={onDone} title="Fertig — senden">
        <VoiceOrb hearing={hearing} size={phone ? 92 : 56} />
      </button>
      <div className="chat-transcript" aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          {interim ? (
            <motion.span key="t" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-on-surface">
              {interim}
              <motion.span className="chat-caret" animate={{ opacity: [1, 0, 1] }} transition={{ duration: 0.9, repeat: Infinity }} />
            </motion.span>
          ) : (
            <motion.span key="h" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-outline">
              {hearing ? 'Ich höre …' : 'Sprich jetzt …'}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      {!phone ? (
        <div className="flex items-center gap-1">
          <button type="button" className="chat-icon-btn" onClick={onCancel} title="Abbrechen">
            <span className="material-symbols-outlined">close</span>
          </button>
          <motion.button whileTap={{ scale: 0.92 }} type="button" className="btn btn-primary chat-listen-btn" onClick={onDone} disabled={!interim}>
            <span className="material-symbols-outlined" style={{ fontSize: '1.1em' }}>arrow_upward</span>Senden
          </motion.button>
        </div>
      ) : (
      <div className="flex items-center gap-2">
        <motion.button whileTap={{ scale: 0.92 }} type="button" className="btn btn-secondary chat-listen-btn" onClick={onCancel}>
          <span className="material-symbols-outlined" style={{ fontSize: '1.1em' }}>close</span>Abbrechen
        </motion.button>
        <motion.button whileTap={{ scale: 0.92 }} type="button" className="btn btn-primary chat-listen-btn" onClick={onDone} disabled={!interim}>
          <span className="material-symbols-outlined" style={{ fontSize: '1.1em' }}>arrow_upward</span>Senden
        </motion.button>
      </div>
      )}
    </motion.div>
  )
}

/**
 * Input row: auto-growing text field + one round action button that morphs between
 * mic (empty field), send (text typed) and a spinner (answer running).
 */
function ChatComposer({ phone, value, onChange, onSend, busy, voice, onFocus, inputRef }) {
  const local = useRef(null)
  const ref = inputRef ?? local
  const hasText = value.trim().length > 0

  // Grow with the text, up to ~5 lines.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, phone ? 132 : 112)}px`
  }, [value, phone, ref])

  const mode = busy ? 'busy' : hasText || !voice.supported ? 'send' : 'mic'
  const act = () => {
    if (mode === 'send') onSend(value)
    else if (mode === 'mic') voice.start()
  }

  return (
    <motion.div layout transition={{ layout: SPRING }} className={`chat-composer ${phone ? 'chat-composer-lg' : ''}`}>
      <AnimatePresence mode="popLayout" initial={false}>
        {voice.listening ? (
          <Listening
            phone={phone}
            hearing={voice.hearing}
            interim={voice.interim}
            onDone={voice.stop}
            onCancel={voice.cancel}
          />
        ) : (
          <motion.div
            key="type"
            className="chat-input-row"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
          >
            <textarea
              id="chat-input"
              ref={ref}
              rows={1}
              value={value}
              onChange={e => onChange(e.target.value)}
              onFocus={onFocus}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (!busy) onSend(value) }
              }}
              enterKeyHint="send"
              placeholder={phone && voice.supported ? 'Schreiben oder sprechen …' : 'Frag YOKAN …'}
              className="chat-input"
            />
            {voice.supported && hasText && !busy && (
              <motion.button
                type="button"
                className="chat-icon-btn"
                title="Sprechen"
                onClick={voice.start}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={SWAP}
              >
                <span className="material-symbols-outlined">mic</span>
              </motion.button>
            )}
            <motion.button
              type="button"
              onClick={act}
              disabled={mode === 'busy'}
              title={mode === 'mic' ? 'Sprechen' : mode === 'send' ? 'Senden' : 'Antwort läuft'}
              className={`chat-action-btn ${phone ? 'chat-action-btn-lg' : ''} ${mode === 'mic' ? 'is-mic' : ''} ${mode === 'send' && hasText ? 'is-ready' : ''}`}
              whileTap={{ scale: 0.88 }}
              layout
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={mode}
                  className={`material-symbols-outlined ${mode === 'busy' ? 'animate-spin' : ''}`}
                  initial={{ scale: 0.3, rotate: -90, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  exit={{ scale: 0.3, rotate: 90, opacity: 0 }}
                  transition={SWAP}
                >
                  {mode === 'mic' ? 'mic' : mode === 'send' ? 'arrow_upward' : 'progress_activity'}
                </motion.span>
              </AnimatePresence>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default memo(ChatComposer)
